import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import debounce from "lodash.debounce";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { API_DATE_FORMAT } from "../constants/dateFormat";
import { TimeboundKeys } from "../types";
import {
  getTimeboundsQueryString,
  getTimelineMiddle,
  getTimelineVisibleDate,
} from "../utils/fast";

import type { JSX } from "react";

import type { Timebounds } from "../types";

const DEBOUNCE_TIME = 500;
const YEAR_LIMIT = 10;

export interface UseTimebounds {
  timelineStartDate: number;
  timelineEndDate: number;
  updateTimebounds: ({
    startTime,
    endTime,
    isDebounced,
  }: Timebounds & { isDebounced?: boolean }) => void;
  getTimebounds: () => Timebounds;
  currentDay: string;
  getCurrentDay: () => string;
}

export interface TimeboundsContextValue {
  setContextTimebounds: ({ startTime, endTime }: Timebounds) => void;
  contextTimebounds: Timebounds;
}

export const TimeboundsContext = createContext<TimeboundsContextValue>({
  setContextTimebounds: () => {},
  contextTimebounds: {
    startTime: 0,
    endTime: 0,
  },
});

function getTimebounds(searchParams: URLSearchParams): {
  startTime: number;
  endTime: number;
} {
  const timeStart = searchParams.get(TimeboundKeys.StartTime);
  const timeEnd = searchParams.get(TimeboundKeys.EndTime);

  if (typeof timeStart !== "string" || typeof timeEnd !== "string") {
    const initialTimelineDate = getTimelineVisibleDate(moment());

    return {
      startTime: initialTimelineDate.start,
      endTime: initialTimelineDate.end,
    };
  }

  const startTime = parseInt(timeStart, 10);
  const endTime = parseInt(timeEnd, 10);

  return {
    startTime,
    endTime,
  };
}

function getCurrentDay(searchParams: URLSearchParams): string {
  const timebounds = getTimebounds(searchParams);

  const timelineMiddle = getTimelineMiddle({
    startTime: timebounds.startTime,
    endTime: timebounds.endTime,
  });

  const currentDay = moment(timelineMiddle).format(API_DATE_FORMAT);

  return currentDay;
}

export function TimeboundsProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const timebounds = useMemo(() => getTimebounds(searchParams), [searchParams]);

  const [stateTimebounds, setStateTimebounds] = useState<Timebounds>({
    startTime: timebounds.startTime,
    endTime: timebounds.endTime,
  });

  const value = useMemo(
    () => ({
      setContextTimebounds: ({ startTime, endTime }: Timebounds): void => {
        setStateTimebounds({ startTime, endTime });
      },
      contextTimebounds: stateTimebounds,
    }),
    [stateTimebounds],
  );

  return (
    <TimeboundsContext.Provider value={value}>
      {children}
    </TimeboundsContext.Provider>
  );
}

export default function useTimebounds(): UseTimebounds {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const { contextTimebounds, setContextTimebounds } =
    useContext(TimeboundsContext);

  useEffect(() => {
    const schedulingPathRegex =
      /^\/fast-channels\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/scheduling/;
    const isSchedulingPath = schedulingPathRegex.test(pathname);

    if (
      isSchedulingPath &&
      (!searchParams.has(TimeboundKeys.StartTime) ||
        !searchParams.has(TimeboundKeys.EndTime))
    ) {
      const timeboundsQuery = getTimeboundsQueryString(new Date());
      history.replace(timeboundsQuery);
    }
  }, [history, pathname, searchParams]);

  const replaceHistory = useCallback(
    ({ startTime, endTime }: { startTime: number; endTime: number }): void =>
      history.replace(
        `?${TimeboundKeys.StartTime}=${startTime}&${TimeboundKeys.EndTime}=${endTime}`,
      ),
    [history],
  );

  const debouncedReplaceHistory = useMemo(
    () =>
      debounce(({ startTime, endTime }) => {
        replaceHistory({ startTime, endTime });
      }, DEBOUNCE_TIME),
    [replaceHistory],
  );

  const updateTimebounds = useCallback(
    ({
      startTime,
      endTime,
      isDebounced,
    }: Timebounds & { isDebounced?: boolean }): void => {
      const limitInFuture = moment().add(YEAR_LIMIT, "years");
      const limitInPast = moment().subtract(YEAR_LIMIT, "years");

      const isOffLimit =
        moment(startTime).isAfter(limitInFuture) ||
        moment(endTime).isAfter(limitInFuture) ||
        moment(startTime).isBefore(limitInPast) ||
        moment(endTime).isBefore(limitInPast);

      const limitedStart = isOffLimit
        ? moment().subtract(1, "hours").valueOf()
        : startTime;

      const limitedEnd = isOffLimit
        ? moment().add(11, "hours").valueOf()
        : endTime;

      setContextTimebounds({ startTime: limitedStart, endTime: limitedEnd });

      if (isDebounced) {
        debouncedReplaceHistory({
          startTime: limitedStart,
          endTime: limitedEnd,
        });
      } else {
        replaceHistory({ startTime: limitedStart, endTime: limitedEnd });
      }
    },
    [debouncedReplaceHistory, replaceHistory, setContextTimebounds],
  );

  const timelineMiddle = getTimelineMiddle({
    startTime: contextTimebounds.startTime,
    endTime: contextTimebounds.endTime,
  });

  const currentDay = useMemo(() => {
    if (contextTimebounds === undefined) {
      throw new Error("useFast must be used within a FASTProvider");
    }
    return moment(timelineMiddle).format(API_DATE_FORMAT);
  }, [timelineMiddle, contextTimebounds]);

  return {
    timelineStartDate: contextTimebounds.startTime,
    timelineEndDate: contextTimebounds.endTime,
    updateTimebounds,
    getCurrentDay: () => getCurrentDay(searchParams),
    getTimebounds: () => getTimebounds(searchParams),
    currentDay,
  };
}
